
import Vue, { PropType } from 'vue';
import ProductPreview from './components/ProductPreview.vue';
import ProductPreviewModal from './components/ProductPreviewModal.vue';
import { mapState, mapActions } from 'pinia';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useColorManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { CvButton } from '@carbon/vue';
import Save16 from '@carbon/icons-vue/es/save/16';
import { PdpChoicesSetterType } from '@/setters/typings';
import { Personalization } from '@midocean/webapps-shared-domain';
import {
  trySavePersonalization,
  triggerSmallDetailsCheck,
} from '@/utils/personalizationHelperV2';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { SwitchColor } from '../SwitchColor';
import GeneratePdf16 from '@carbon/icons-vue/es/generate-pdf/16';
import Maximize16 from '@carbon/icons-vue/es/maximize/16';
import { getIntershopRedirectionUrl } from '@/utils/index';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $isConceptDisabled: boolean;
    $isOrderDisabled: boolean;
    $customerCompanyLogoUrl: string;
    $context: string;
    $userLoginOptions: { data: string; iv: string };
    $isCommunicationSentToCC: boolean;
    $notifyCustomerOnFirstProof: boolean;
    $customerCareEmail: string;
  }
}

export default Vue.extend({
  name: 'ProductManagement',

  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$isConceptDisabled',
    '$isOrderDisabled',
    '$customerCompanyLogoUrl',
    '$context',
    '$userLoginOptions',
    '$isCommunicationSentToCC',
    '$notifyCustomerOnFirstProof',
    '$customerCareEmail',
  ],

  components: {
    ProductPreview,
    ProductPreviewModal,
    SwitchColor,
    CvButton,
    GeneratePdf16,
    Maximize16,
  },

  data() {
    return {
      isPreviewModalOpen: false,
      isPreviewHighlighted: false,
      Save16,
      getIntershopRedirectionUrl,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'isAnyPositionAdded',
    ]),
    ...mapState(useNotificationStore, [
      'redAndOrangeNotifications',
      'hasRedNotifications',
    ]),
    ...mapState(useShapeStore, ['shapes']),
    ...mapState(useGlobalStore, ['isProcessingOrder', 'isLoaderActive']),
    ...mapState(useLangStore, ['languageInUse']),

    isSaveActionDisabled(): boolean {
      return (
        this.shapes.length === 0 ||
        this.isProcessingOrder ||
        this.hasErrorsBesideSmallDetails ||
        this.hasEmptyTexts ||
        this.isAnyPositionMissingColors() ||
        !this.isAnyPositionAdded
      );
    },
    hasEmptyTexts(): boolean {
      return this.shapes.some(
        (shape) => shape.kind === 'Text' && shape.w2pText.text === ''
      );
    },
    hasErrorsBesideSmallDetails(): boolean {
      return (
        this.hasRedNotifications &&
        this.redAndOrangeNotifications.some(
          (error) =>
            error.validatorName !== 'smallDetails' &&
            error.validatorColor === 'Red'
        )
      );
    },
  },

  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    ...mapActions(useColorManagementStore, ['isAnyPositionMissingColors']),

    async handleSaveConcept() {
      if (DoSmallDetailsPart && triggerSmallDetailsCheck()) {
        this.$emit('action:trigger-small-details');
      } else {
        this.setIsProcessingOrder(true);
        this.setIsLoaderActive(true);
        try {
          const personalizationId = await trySavePersonalization(
            this.$isEditMode,
            this.$pdpChoices.productVariants,
            this.$sapCustomerId,
            true,
            this.$isDirectApproval,
            this.$customerCompanyLogoUrl,
            this.languageInUse,
            this.$isCommunicationSentToCC,
            this.$notifyCustomerOnFirstProof,
            this.$customerCareEmail
          );
          if (this.$context === 'CustomerCare') {
            const url = this.getIntershopRedirectionUrl(
              personalizationId,
              true,
              this.$userLoginOptions.data,
              this.$userLoginOptions.iv
            );
            window.open(url, '_blank');
          }

          this.$emit('wizard:saved-as-concept', { personalizationId });
          this.$emit('action:disable-pintura');
        } catch (e: unknown) {
          console.log((e as Error).message);
        } finally {
          this.setIsProcessingOrder(false);
          this.setIsLoaderActive(false);
        }
      }
    },
  },
});
